/**
 * W-061 — Notification history. Stories: US-A08 AC6.
 * When it was sent, to whom, by whom, and the delivery statistics.
 */
import { List, useTable } from '@refinedev/antd'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import type { NotificationRow } from '@/types'

export const NotificationHistoryPage = () => {
  const translate = useAdminTranslate()
  const { tableProps } = useTable<NotificationRow>({
    // TODO(US-A08): `notifications` holds one row per recipient (docs/03 §4.2,
    // `user_id` null means broadcast). History needs one row per campaign with
    // its delivery count — the backend agent owns a `notification_campaigns`
    // view or table; this is its working name.
    resource: 'notification_campaigns',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 25 },
  })

  return (
    <List title={translate('admin.pages.notificationHistory')} canCreate={false}>
      <Table<NotificationRow>
        {...tableProps}
        rowKey="id"
        expandable={{
          expandedRowRender: record => (
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <div dir="rtl">{record.body_ar}</div>
              <div>{record.body_fr}</div>
            </Typography.Paragraph>
          ),
        }}
        columns={[
          {
            title: translate('admin.screens.notificationHistory.sentAt'),
            dataIndex: 'created_at',
            render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
          },
          { title: translate('admin.columns.titleFr'), dataIndex: 'title_fr' },
          {
            title: translate('admin.columns.titleAr'),
            dataIndex: 'title_ar',
            render: (value: string) => <span dir="rtl">{value}</span>,
          },
          {
            title: translate('admin.screens.notificationHistory.audience'),
            dataIndex: 'audience',
            render: (value: string | undefined) => (
              <Tag>{value ?? translate('admin.screens.notificationHistory.audienceAll')}</Tag>
            ),
          },
          {
            title: translate('admin.screens.compose.devicesReached'),
            dataIndex: 'delivered_count',
            render: (value: number | undefined) => value ?? '—',
          },
          {
            title: translate('admin.screens.notificationHistory.sentBy'),
            dataIndex: 'sent_by',
            render: (value: string | undefined) => value ?? '—',
          },
        ]}
      />
    </List>
  )
}
